/**
 * Which store *this* browser's device buys from.
 *
 * The free web build shows both official badges, because a browser player could be on either
 * platform and a wrong guess puts a Google Play badge in front of an iPhone. That is the right
 * default for a page someone is browsing — but it is the wrong answer at the one moment the demo
 * asks for the sale, where two badges are a question and one button is an answer.
 *
 * So this module answers a narrower question than `src/platform` does. `detectPlatformId` asks
 * "which shell am I running inside" (web, TWA, iOS, clip) and is deliberately conservative;
 * this asks "if this web visitor bought the app, where would they buy it", and is allowed to
 * return **null** — the honest answer for a desktop, and the one that keeps both badges.
 *
 * Nothing here changes what the game does. It decides a link, and (via
 * `demoRunsToSaturn()` in src/game/clip.ts) whether the demo can end on a store button at all.
 */

export type StoreTarget = 'appstore' | 'googleplay';

/** Everything detection looks at, passed in so the rules can be tested without a DOM. */
export interface DeviceSignals {
  /** navigator.userAgent */
  ua: string;
  /** navigator.maxTouchPoints — the only way to tell an iPad from a Mac (see below). */
  maxTouchPoints: number;
  /** navigator.userAgentData?.platform, where the browser offers it. */
  uaDataPlatform?: string;
  /** location.search — a campaign may state the platform outright; see STORE_PARAM. */
  search?: string;
}

/**
 * `?store=ios|android` on the landing URL, which beats every guess below.
 *
 * The demo's traffic comes from campaigns that already TARGET an OS, so the ad platform knows the
 * answer for certain while we are still sniffing a string. One parameter on the landing URL turns
 * a guess into a fact, and costs the campaign nothing.
 */
export const STORE_PARAM = 'store';

const PARAM_VALUES: Record<string, StoreTarget> = {
  ios: 'appstore',
  iphone: 'appstore',
  ipad: 'appstore',
  appstore: 'appstore',
  apple: 'appstore',
  android: 'googleplay',
  play: 'googleplay',
  googleplay: 'googleplay',
};

/**
 * The store this device buys from, or null when we cannot tell.
 *
 * Order matters. The explicit parameter wins; then Android, which every Android browser says
 * plainly; then iOS. The last rule is the one that is easy to miss: **iPadOS 13+ Safari reports
 * itself as a Macintosh**, and only `maxTouchPoints` separates it from a real Mac. Without that
 * rule every iPad falls through to null and is offered two badges on a device that can only use
 * one of them.
 *
 * Everything else — desktop, a browser we do not recognise — is null on purpose. "I don't know"
 * is a real answer here and it has a good fallback (both badges), which a wrong guess does not.
 */
export function detectStoreTarget(s: DeviceSignals): StoreTarget | null {
  const stated = new URLSearchParams(s.search ?? '').get(STORE_PARAM);
  if (stated) {
    const known = PARAM_VALUES[stated.toLowerCase()];
    if (known) return known;
  }

  if (s.uaDataPlatform === 'Android') return 'googleplay';
  if (/Android/i.test(s.ua)) return 'googleplay';

  if (/iPhone|iPad|iPod/i.test(s.ua)) return 'appstore';
  if (/Macintosh/i.test(s.ua) && s.maxTouchPoints > 1) return 'appstore';

  return null;
}

interface UaData { platform?: string }

/** The live answer for this browser. Null in a non-browser environment, like every node test. */
export function storeTarget(): StoreTarget | null {
  if (typeof navigator === 'undefined') return null;
  const uaData = (navigator as Navigator & { userAgentData?: UaData }).userAgentData;
  return detectStoreTarget({
    ua: navigator.userAgent ?? '',
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    uaDataPlatform: uaData?.platform,
    search: typeof location === 'undefined' ? '' : location.search,
  });
}
